import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Checkbox, Drawer, Modal, Progress, Select, Space, Table, Tag, message } from "antd";
import moment from "moment";
import { Body } from "../../../components/main-body/body";
import ApiProjectClones from "../../../api/project-clones";
import ApiProjects from "../../../api/projects";
import "./project-clone-debug.css";

const STATUS_OPTIONS = [
  "queued",
  "running",
  "completed",
  "failed",
  "canceled",
].map((value) => ({ label: value, value }));

const TYPE_OPTIONS = [
  { label: "clone", value: "clone" },
  { label: "create", value: "create" },
];

const STATUS_COLORS = {
  queued: "gold",
  running: "blue",
  completed: "green",
  failed: "red",
  canceled: "default",
};

const LIST_POLL_MS = 5000;
const DETAIL_POLL_MS = 3000;

function formatDate(value) {
  return value ? `${moment(value).utc().format("DD MMM YYYY, HH:mm:ss")}, UTC` : "-";
}

function formatDuration(ms) {
  if (ms === null || ms === undefined) return "-";

  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours) return `${hours}h ${minutes}m ${seconds}s`;
  if (minutes) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatProject(project, fallbackId) {
  if (project) {
    return `${project.code || project.id}${project.title ? ` - ${project.title}` : ""}`;
  }

  return fallbackId || "-";
}

function stringify(value) {
  if (!value) return "-";

  try {
    return JSON.stringify(value, null, 2);
  } catch (error) {
    return String(value);
  }
}

function isActiveStatus(status) {
  return status === "queued" || status === "running";
}

const ProjectCloneDebug = () => {
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [summary, setSummary] = useState({ statuses: [] });
  const [filters, setFilters] = useState({ status: undefined, type: undefined });
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Oversized base64 covers in Page.image are the known clone-killer; this
  // section drives the /admin/pages/compress-covers backfill from the UI.
  const [covers, setCovers] = useState(null);
  const [coversLoading, setCoversLoading] = useState(false);

  const runCovers = async (dryRun) => {
    setCoversLoading(true);

    try {
      const response = await ApiProjectClones.compressPageCovers({
        dryRun: dryRun ? "true" : undefined,
        limit: dryRun ? 50 : 20,
      });
      setCovers(response.data);

      if (!dryRun) {
        message.success(`Compressed ${response.data.compressed}, remaining ${response.data.remaining}`);
      }
    } catch (error) {
      console.error("Compress covers failed:", error);
      message.error(error.response?.data?.error || "Failed to process covers");
    } finally {
      setCoversLoading(false);
    }
  };

  const logsEndRef = useRef(null);
  const detailRef = useRef(null);
  detailRef.current = detail;

  const loadData = useCallback(async (page = pagination.page, limit = pagination.limit, silent = false) => {
    if (!silent) setLoading(true);

    try {
      const query = {
        page,
        limit,
        status: filters.status || undefined,
        type: filters.type || undefined,
      };

      const response = await ApiProjectClones.getProjectClones(query);
      setRows(response.data.operations || []);
      setPagination(response.data.pagination || { page, limit, total: 0 });
      setSummary(response.data.summary || { statuses: [] });
    } catch (error) {
      console.error("Project clones load failed:", error);
      if (!silent) {
        message.error(error.response?.data?.error || "Failed to load clone operations");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [filters, pagination.limit, pagination.page]);

  useEffect(() => {
    loadData(1, pagination.limit);
  }, [filters, loadData, pagination.limit]);

  // Keep the list fresh while something is in flight.
  useEffect(() => {
    if (!autoRefresh) return undefined;

    const timer = setInterval(() => {
      loadData(undefined, undefined, true);
    }, LIST_POLL_MS);

    return () => clearInterval(timer);
  }, [autoRefresh, loadData]);

  const loadDetail = useCallback(async (id) => {
    setDetailLoading(true);

    try {
      const response = await ApiProjectClones.getProjectClone(id);
      setDetail(response.data);
    } catch (error) {
      console.error("Clone detail load failed:", error);
      message.error(error.response?.data?.error || "Failed to load clone operation");
    } finally {
      setDetailLoading(false);
    }
  }, []);

  // Tail the logs of an open operation while it is still active.
  useEffect(() => {
    if (!detailId) return undefined;

    const timer = setInterval(async () => {
      const current = detailRef.current;
      if (!current || !isActiveStatus(current.operation?.status)) return;

      try {
        const lastLogId = current.logs?.length
          ? current.logs[current.logs.length - 1].id
          : undefined;
        const response = await ApiProjectClones.getProjectClone(detailId, {
          afterId: lastLogId,
        });

        setDetail((prev) => {
          if (!prev) return response.data;

          return {
            ...response.data,
            logs: [...(prev.logs || []), ...(response.data.logs || [])],
          };
        });
      } catch (error) {
        console.error("Clone log tail failed:", error);
      }
    }, DETAIL_POLL_MS);

    return () => clearInterval(timer);
  }, [detailId]);

  useEffect(() => {
    if (logsEndRef.current) {
      logsEndRef.current.scrollIntoView({ block: "end" });
    }
  }, [detail?.logs?.length]);

  const openDetail = (id) => {
    setDetailId(id);
    setDetail(null);
    loadDetail(id);
  };

  const closeDetail = () => {
    setDetailId(null);
    setDetail(null);
  };

  const statusCounts = useMemo(() => {
    const result = {};
    (summary.statuses || []).forEach((item) => {
      result[item.status] = Number(item.count || 0);
    });
    return result;
  }, [summary.statuses]);

  const columns = [
    {
      title: "Started",
      dataIndex: "createdAt",
      width: 175,
      render: formatDate,
    },
    {
      title: "Type",
      dataIndex: "type",
      width: 80,
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      width: 150,
      render: (status, row) => (
        <Space size={4}>
          <Tag color={STATUS_COLORS[status] || "default"}>{status}</Tag>
          {row.isStale && <Tag color="volcano">STUCK?</Tag>}
        </Space>
      ),
    },
    {
      title: "Source → Target",
      key: "projects",
      width: 280,
      render: (_, row) => (
        <div className="project-clone-debug-projects">
          {row.type === "clone" && (
            <span className="project-clone-debug-muted">
              {formatProject(row.sourceProject, row.sourceProjectId)} →
            </span>
          )}
          <span>
            {formatProject(row.targetProject, row.targetProjectId)}
            {row.targetProject?.deletedAt ? " (deleted)" : ""}
          </span>
        </div>
      ),
    },
    {
      title: "Progress",
      dataIndex: "progress",
      width: 220,
      render: (progress, row) => (
        <div>
          <Progress
            percent={Number(progress || 0)}
            size="small"
            status={
              row.status === "failed"
                ? "exception"
                : isActiveStatus(row.status)
                  ? "active"
                  : undefined
            }
          />
          {row.currentStep && (
            <div className="project-clone-debug-step">{row.currentStep}</div>
          )}
        </div>
      ),
    },
    {
      title: "Duration",
      dataIndex: "durationMs",
      width: 110,
      render: formatDuration,
    },
    {
      title: "Heartbeat",
      dataIndex: "secondsSinceHeartbeat",
      width: 120,
      render: (seconds, row) => {
        if (seconds === null || seconds === undefined) return "-";
        if (!isActiveStatus(row.status)) return "-";

        return (
          <span style={row.isStale ? { color: "#cf1322", fontWeight: 600 } : undefined}>
            {seconds}s ago
          </span>
        );
      },
    },
    {
      title: "Initiated by",
      dataIndex: "initiatedBy",
      width: 200,
      render: (user, row) => (user ? user.email : row.initiatedByUserId || "-"),
    },
    {
      title: "Job",
      dataIndex: "jobId",
      width: 90,
      render: (value) => value || "-",
    },
  ];

  const operation = detail?.operation;
  const entityCounts = operation?.meta?.summary?.entityCounts;

  // A broken clone leaves a half-filled project behind — let the admin
  // remove it right from here (same endpoint as the provisioning modal's
  // cancel button; it cancels the job and soft-deletes the project).
  const canDeleteTargetProject = Boolean(
    operation?.targetProject &&
    !operation.targetProject.deletedAt &&
    ["provisioning", "canceling", "failed", "canceled"].includes(
      operation.targetProject.provisionStatus
    )
  );

  const handleDeleteTargetProject = () => {
    const target = operation?.targetProject;
    if (!target) return;

    Modal.confirm({
      title: `Delete project "${target.title || target.code}"?`,
      content:
        "This cancels the clone (if still running) and deletes the half-cloned project. The source project is not affected.",
      okText: "Delete project",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await ApiProjects.cancelProjectProvision(target.id);
          message.success("Broken project deleted");
          loadDetail(detailId);
          loadData(undefined, undefined, true);
        } catch (error) {
          console.error("Delete broken project failed:", error);
          message.error(
            error.response?.data?.error?.message || "Failed to delete project"
          );
        }
      },
    });
  };

  return (
    <Body header={{ title: "Project clone debug" }} scroll>
      <div className="project-clone-debug-page">
        <div className="project-clone-debug-summary">
          {STATUS_OPTIONS.map((option) => (
            <div className="project-clone-debug-summary-item" key={option.value}>
              <div className="project-clone-debug-summary-label">{option.label}</div>
              <div className="project-clone-debug-summary-value">
                {statusCounts[option.value] || 0}
              </div>
            </div>
          ))}
        </div>

        <div className="project-clone-debug-filters">
          <Select
            allowClear
            placeholder="Status"
            value={filters.status}
            options={STATUS_OPTIONS}
            onChange={(value) => setFilters((prev) => ({ ...prev, status: value }))}
          />
          <Select
            allowClear
            placeholder="Type"
            value={filters.type}
            options={TYPE_OPTIONS}
            onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
          />
          <Button onClick={() => loadData()}>Refresh</Button>
          <Checkbox
            checked={autoRefresh}
            onChange={(event) => setAutoRefresh(event.target.checked)}
          >
            Auto-refresh
          </Checkbox>
        </div>

        <div className="project-clone-debug-covers">
          <div className="project-clone-debug-covers-header">
            <div>
              <h4>Oversized article covers</h4>
              <span className="project-clone-debug-muted">
                Articles whose cover is stored as a huge base64 image — the main cause
                of clone failures. Scan to preview, then compress in batches until
                remaining is 0.
              </span>
            </div>
            <Space>
              <Button loading={coversLoading} onClick={() => runCovers(true)}>
                Scan (dry run)
              </Button>
              <Button
                type="primary"
                danger
                loading={coversLoading}
                disabled={!covers || covers.remaining === 0}
                onClick={() => runCovers(false)}
              >
                Compress batch
              </Button>
              {covers && (
                <Tag color={covers.remaining === 0 ? "green" : "volcano"}>
                  remaining: {covers.remaining}
                </Tag>
              )}
            </Space>
          </div>

          {covers && (covers.details || []).length > 0 && (
            <Table
              columns={[
                { title: "Article", dataIndex: "title", ellipsis: true },
                {
                  title: "Project",
                  dataIndex: "projectId",
                  width: 110,
                  render: (value) => (value ? `${value.slice(0, 8)}…` : "-"),
                },
                {
                  title: "Before",
                  dataIndex: "beforeBytes",
                  width: 100,
                  render: (value) => (value ? `${(value / 1048576).toFixed(1)} MB` : "-"),
                },
                {
                  title: "After",
                  dataIndex: "afterBytes",
                  width: 100,
                  render: (value) => (value ? `${Math.round(value / 1024)} KB` : "-"),
                },
                {
                  title: "Status",
                  dataIndex: "compressed",
                  width: 120,
                  render: (value, row) => (
                    <Tag color={value ? "green" : "gold"}>
                      {value ? "compressed" : covers.dryRun ? "candidate" : "skipped"}
                    </Tag>
                  ),
                },
              ]}
              dataSource={covers.details}
              pagination={false}
              rowKey="id"
              size="small"
            />
          )}
        </div>

        <Table
          columns={columns}
          dataSource={rows}
          loading={loading}
          onRow={(row) => ({
            onClick: () => openDetail(row.id),
            style: { cursor: "pointer" },
          })}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            total: pagination.total,
            showSizeChanger: true,
            pageSizeOptions: [25, 50, 100, 200],
            onChange: (page, pageSize) => loadData(page, pageSize),
          }}
          rowKey="id"
          scroll={{ x: 1400 }}
          size="middle"
        />

        <Drawer
          open={Boolean(detailId)}
          onClose={closeDetail}
          size={760}
          title={
            operation ? (
              <Space>
                <Tag color={STATUS_COLORS[operation.status] || "default"}>
                  {operation.status}
                </Tag>
                {operation.isStale && <Tag color="volcano">STUCK?</Tag>}
                <span>
                  {operation.type === "clone" ? "Clone" : "Create"}{" "}
                  {formatProject(operation.targetProject, operation.targetProjectId)}
                </span>
              </Space>
            ) : (
              "Clone operation"
            )
          }
        >
          {detailLoading && !operation && <span>Loading…</span>}

          {operation && (
            <div className="project-clone-debug-detail">
              <div className="project-clone-debug-detail-grid">
                <div>
                  <div className="project-clone-debug-summary-label">Operation</div>
                  <div>{operation.id}</div>
                </div>
                <div>
                  <div className="project-clone-debug-summary-label">Queued</div>
                  <div>{formatDate(operation.queuedAt)}</div>
                </div>
                <div>
                  <div className="project-clone-debug-summary-label">Started</div>
                  <div>{formatDate(operation.startedAt)}</div>
                </div>
                <div>
                  <div className="project-clone-debug-summary-label">Finished</div>
                  <div>{formatDate(operation.finishedAt)}</div>
                </div>
                <div>
                  <div className="project-clone-debug-summary-label">Duration</div>
                  <div>{formatDuration(operation.durationMs)}</div>
                </div>
                <div>
                  <div className="project-clone-debug-summary-label">Progress</div>
                  <div>
                    {operation.progress}%
                    {operation.currentStep ? ` — ${operation.currentStep}` : ""}
                  </div>
                </div>
                <div>
                  <div className="project-clone-debug-summary-label">Initiated by</div>
                  <div>{operation.initiatedBy?.email || operation.initiatedByUserId || "-"}</div>
                </div>
                <div>
                  <div className="project-clone-debug-summary-label">Bull job</div>
                  <div>
                    {detail.queueJob
                      ? `#${detail.queueJob.id} (${detail.queueJob.state})`
                      : operation.jobId
                        ? `#${operation.jobId} (not in queue anymore)`
                        : "-"}
                  </div>
                </div>
              </div>

              {canDeleteTargetProject && (
                <div>
                  <Button danger onClick={handleDeleteTargetProject}>
                    Delete broken project
                  </Button>
                </div>
              )}

              {detail.queueJob?.failedReason && (
                <div>
                  <h4>Queue failure reason</h4>
                  <pre>{detail.queueJob.failedReason}</pre>
                </div>
              )}

              {operation.error && (
                <div>
                  <h4>Error</h4>
                  <pre>{operation.error}{operation.errorStack ? `\n\n${operation.errorStack}` : ""}</pre>
                </div>
              )}

              {entityCounts && (
                <div>
                  <h4>Copied entities</h4>
                  <pre>{stringify(entityCounts)}</pre>
                </div>
              )}

              <div>
                <h4>
                  Logs ({detail.logs?.length || 0})
                  {isActiveStatus(operation.status) && (
                    <span className="project-clone-debug-muted"> — live, updates every 3s</span>
                  )}
                </h4>
                <div className="project-clone-debug-logs">
                  {(detail.logs || []).map((log) => (
                    <div
                      key={log.id}
                      className={`project-clone-debug-log-row level-${log.level}`}
                    >
                      <span className="project-clone-debug-log-time">
                        {moment(log.createdAt).utc().format("HH:mm:ss.SSS")}
                      </span>
                      {log.step && (
                        <span className="project-clone-debug-log-step">[{log.step}]</span>
                      )}
                      <span className="project-clone-debug-log-message">{log.message}</span>
                    </div>
                  ))}
                  {(detail.logs || []).length === 0 && (
                    <span className="project-clone-debug-muted">No log entries</span>
                  )}
                  <div ref={logsEndRef} />
                </div>
              </div>
            </div>
          )}
        </Drawer>
      </div>
    </Body>
  );
};

export default ProjectCloneDebug;
